import React, { useState } from 'react';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import './index.less';

import { ProjectModel, PromptMode, ProviderModelType } from '../../../../../gamma-api';
import IbCard from '../../../../components/common/IbCard';
import IbContextMenu, { IIbContextMenuItem } from '../../../../components/common/IbContextMenu';
import IbButton from '../../../../components/common/IbButton';
import IbIcon from '../../../../components/common/IbIcon';
import IbTypography from '../../../../components/common/IbTypography';
import { formatDateFull, getTranslateFunc } from '../../../../utils/stringUtil';
import ImportUpload from '../../../../components/ImportUpload';
import ExportButton from '../../../../components/ExportButton';
import ModelTag from '../../../../components/ModelTag';
import CardContent, { ICardContentItem } from '../../../../components/CardContent';
import { getProjectPath } from '../../../../utils/pathUtils';
import { getSourceItemName, getSourceTypeIconName } from '../../../../utils/ingestionSourceUtil';
import { RESPONSE_GENERATION_HASH } from '../../../../constants';

import {
  EXTRA_CLASS_NAME,
  FOOTER_CLASS_NAME,
  FOOTER_MODELS_CLASS_NAME,
  MAIN_CLASS_NAME,
  SOURCES_PAGE_SIZE,
  TRANSLATION_GROUP_PREFIX,
} from './const';

interface IProjectCardProps {
  project: ProjectModel;
  searchText: string;
  onEditProject: () => void;
  onDeleteProject: () => void;
  onProjectClick: () => void;
}

const ProjectCard: React.FC<IProjectCardProps> = ({
  project,
  searchText,
  onEditProject,
  onDeleteProject,
  onProjectClick,
}) => {
  const translate = getTranslateFunc(TRANSLATION_GROUP_PREFIX);
  const { i18n } = useTranslation();

  const { push } = useHistory();

  const [contextMenuVisible, setContextMenuVisible] = useState(false);

  const goToProject = () => {
    push(getProjectPath(project.folder_id, project.project_id));
  };

  const onImportFileFinish = async () => {
    goToProject();
  };

  const contextMenuItems = [
    {
      icon: <IbIcon iconName="efferent-three" size={20} />,
      text: translate('Context: Export'),
      render: (child) => (
        <ExportButton.ProjectExport projectId={project.project_id}>{child}</ExportButton.ProjectExport>
      ),
    },
    {
      icon: <IbIcon iconName="afferent-three" size={20} />,
      text: translate('Context: Import'),
      render: (child) => (
        <ImportUpload.ProjectImport
          folderId={project.folder_id}
          projectId={project.project_id}
          onFinish={onImportFileFinish}
        >
          {child}
        </ImportUpload.ProjectImport>
      ),
    },
    {
      icon: <IbIcon iconName="edit" size={20} />,
      onSelect: onEditProject,
      text: translate('Context: Edit'),
    },
    {
      icon: <IbIcon iconName="delete" size={20} />,
      onSelect: onDeleteProject,
      text: translate('Context: Delete'),
    },
  ] as IIbContextMenuItem[];

  const renderExtra = () => {
    const onContextMenuClose = () => setContextMenuVisible(false);
    const onMenuButtonClick = () => setContextMenuVisible(true);
    return (
      <div
        className={EXTRA_CLASS_NAME}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <IbContextMenu menuItems={contextMenuItems} visible={contextMenuVisible} onClose={onContextMenuClose}>
          <IbButton icon={<IbIcon iconName="more-one" size={20} />} type="icon" onClick={onMenuButtonClick} />
        </IbContextMenu>
      </div>
    );
  };

  const getInstructions = () => {
    const synthesize = project.pipeline.search?.synthesize;
    if (synthesize?.prompt_mode === PromptMode.Advanced) {
      return synthesize.advanced_prompts?.context_prompt || '';
    }
    return synthesize?.simple_prompts?.prompt || '';
  };

  const onShowAllSourcesButtonClick = () => {
    push(getProjectPath(project.folder_id, project.project_id));
  };

  const onPromptClick = () => {
    push(`${getProjectPath(project.folder_id, project.project_id, 'search')}${RESPONSE_GENERATION_HASH}`);
  };

  const renderContent = () => {
    const items: ICardContentItem[] = project.sources.slice(0, SOURCES_PAGE_SIZE).map((source) => ({
      key: source.source_id,
      iconName: getSourceTypeIconName(source.type),
      displayName: getSourceItemName(source),
    }));

    return (
      <CardContent
        instructions={getInstructions()}
        items={items}
        itemsLabel={translate('Sources: All', { count: project.sources.length })}
        onShowAllItems={onShowAllSourcesButtonClick}
        onShowFullInstructions={onPromptClick}
      />
    );
  };

  const renderFooter = () => {
    return (
      <div className={FOOTER_CLASS_NAME}>
        <IbTypography.Paragraph disabled type="descriptor">
          {translate('Modified on', { date: formatDateFull(project.modified_on, i18n.language) })}
        </IbTypography.Paragraph>
        <div className={FOOTER_MODELS_CLASS_NAME}>
          <ModelTag modelName={project.pipeline.ingestion?.embedding?.model_name} type={ProviderModelType.Embedding} />
          <ModelTag modelName={project.pipeline.search?.llm?.model_name} />
        </div>
      </div>
    );
  };

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbCard
        extra={renderExtra()}
        footer={renderFooter()}
        searchText={searchText}
        title={project.name}
        onClick={onProjectClick}
      >
        {renderContent()}
      </IbCard>
    </div>
  );
};

export default ProjectCard;
